import { makeChangePasswordPageForm, toChangePasswordBody } from "@seagames/contracts";
import { createFileRoute, redirect as routerRedirect, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	AuthFrame,
	AuthHeading,
	Field,
	FormAlert,
	KeyGlyph,
	LinkButton,
	SubmitButton,
	fieldIconClass,
} from "../components/auth/auth-frame";
import { ApiError } from "../lib/api/client";
import { errorMessage } from "../lib/api/messages";
import { changePassword, logout, meQueryOptions } from "../lib/auth/session";
import { hasStoredSession } from "../lib/auth/token-store";

/**
 * Where a session with `must_change_password` lands. The SERVER enforces it
 * (403 PASSWORD_CHANGE_REQUIRED everywhere else); this page is only the way out.
 *
 * The forced first change asks only New + Confirm — they just signed in with
 * the temporary password (user, 2026-11-04). The SERVER decides that from the
 * token's `pwc` claim; `/auth/me` only decides which boxes to draw. Not known
 * (no answer) → the Current box is drawn: a forced change ignores it.
 */
const search = z.object({
	redirect: z
		.string()
		.optional()
		.transform((v) => (v && v.startsWith("/") && !v.startsWith("//") ? v : undefined)),
});

export const Route = createFileRoute("/change-password")({
	validateSearch: search,
	beforeLoad: async ({ context, location }) => {
		if (!hasStoredSession()) throw routerRedirect({ to: "/login", search: { redirect: location.href } });
		const forced = await context.queryClient.ensureQueryData(meQueryOptions).then(
			(me) => me.must_change_password,
			() => false,
		);
		return { forced };
	},
	component: ChangePasswordPage,
});

type Errors = { current?: string; next?: string; confirm?: string };

function ChangePasswordPage() {
	const { t } = useTranslation("auth");
	const tc = useTranslation().t;
	const { redirect } = Route.useSearch();
	const { forced } = Route.useRouteContext();
	const navigate = useNavigate();
	const schema = useMemo(
		() =>
			makeChangePasswordPageForm(forced, {
				currentPasswordRequired: t("validation.currentPasswordRequired"),
				newPasswordMin: t("validation.newPasswordMin"),
				newPasswordSame: t("validation.newPasswordSame"),
				confirmPasswordMismatch: t("validation.confirmPasswordMismatch"),
			}),
		[t, forced],
	);
	const [current, setCurrent] = useState("");
	const [next, setNext] = useState("");
	const [confirm, setConfirm] = useState("");
	const [errors, setErrors] = useState<Errors>({});
	const [serverError, setServerError] = useState<unknown>(null);
	const [pending, setPending] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		const parsed = schema.safeParse({
			...(forced ? {} : { current_password: current }),
			new_password: next,
			confirm_password: confirm,
		});
		if (!parsed.success) {
			const f = parsed.error.flatten().fieldErrors;
			setErrors({ current: f.current_password?.[0], next: f.new_password?.[0], confirm: f.confirm_password?.[0] });
			return;
		}
		setErrors({});
		setServerError(null);
		setPending(true);
		try {
			await changePassword(toChangePasswordBody(parsed.data, forced));
			void navigate({ to: redirect ?? "/", replace: true });
		} catch (err) {
			if (err instanceof ApiError && err.code === "INVALID_CREDENTIALS") {
				setErrors({ current: t("changePassword.wrongCurrent") });
			} else if (err instanceof ApiError && err.code === "VALIDATION_FAILED" && err.fieldErrors["new_password"]) {
				// The forced change refuses keeping the temporary password.
				setErrors({ next: forced ? t("changePassword.sameAsTemporary") : err.fieldErrors["new_password"][0] });
			} else {
				setServerError(err);
			}
		} finally {
			setPending(false);
		}
	};

	return (
		<AuthFrame>
			<AuthHeading title={forced ? t("changePassword.title") : t("changePassword.titleVoluntary")}>
				{forced ? t("changePassword.subtitle") : t("changePassword.subtitleVoluntary")}
			</AuthHeading>
			{serverError != null && <FormAlert>{errorMessage(serverError)}</FormAlert>}
			<form onSubmit={submit} noValidate className="grid gap-[1rem]">
				{!forced && (
					<Field
						label={t("changePassword.current")}
						type="password"
						autoComplete="current-password"
						autoFocus
						icon={<KeyGlyph className={fieldIconClass} />}
						value={current}
						onChange={(e) => setCurrent(e.target.value)}
						error={errors.current}
					/>
				)}
				<Field
					label={t("changePassword.new")}
					hint={t("changePassword.newHint")}
					type="password"
					autoComplete="new-password"
					autoFocus={forced}
					icon={<KeyGlyph className={fieldIconClass} />}
					value={next}
					onChange={(e) => setNext(e.target.value)}
					error={errors.next}
				/>
				<Field
					label={t("changePassword.confirm")}
					type="password"
					autoComplete="new-password"
					icon={<KeyGlyph className={fieldIconClass} />}
					value={confirm}
					onChange={(e) => setConfirm(e.target.value)}
					error={errors.confirm}
				/>
				<SubmitButton pending={pending}>{t("changePassword.submit")}</SubmitButton>
				<LinkButton
					disabled={pending}
					onClick={async () => {
						await logout();
						await navigate({ to: "/login", search: { reason: "user" } });
					}}
				>
					{tc("actions.signOut")}
				</LinkButton>
			</form>
		</AuthFrame>
	);
}
